import { SkillModule } from '../types/pathway'

export const skillModules: SkillModule[] = [
  {
    id: 'leading-through-change',
    courseId: 'course-v1:UOPX+LTCSKv1+Q22024',
    name: 'Leading Through Change',
    category: 'Change Management',
    description:
      'Your leadership team can explore effective leadership roles and styles to build and maintain trust throughout the change process.',
    relevance:
      'Teaches leaders how to guide teams through change using different leadership styles and decision-making.',
    topics: [
      'Decision-making',
      'Impact of inspirational leadership',
      'Role of leadership in motivation',
      'Leading by example',
      'Leadership styles in change management',
    ],
    outcomes: [
      'Articulate the critical functions leadership plays in managing organizational change.',
      'Analyze change scenarios to identify which leadership style works best.',
      'Describe how leaders set a strong example to guide teams during change.',
    ],
    durationHours: 2.5,
    levels: ['new-manager', 'mid-level', 'senior-leader'],
    keywords: ['change management', 'leadership', 'decision making', 'retention'],
  },
  {
    id: 'communicating-change',
    courseId: 'course-v1:UOPX+CCSKv1+Q22024',
    name: 'Communicating Change',
    category: 'Change Management',
    description:
      'Employees can learn to identify effective feedback mechanisms and bidirectional communication tools for real-world change management applications.',
    relevance:
      'Directly teaches the communication strategies essential to successful change initiatives.',
    topics: [
      'Principles of effective communication',
      'Crafting change messages',
      'Choosing the right communication channels',
      'Developing a communication plan',
      'Feedback mechanisms and two-way communication',
    ],
    outcomes: [
      'Explain how effective communication influences change management results.',
      'Describe the critical components of a change communication plan.',
      'Identify suitable feedback mechanisms for change management contexts.',
    ],
    durationHours: 2,
    levels: ['mid-level', 'senior-leader'],
    keywords: ['change management', 'communication', 'stakeholder management'],
  },
  {
    id: 'managing-resistance',
    courseId: 'course-v1:UOPX+MRCSKv1+Q32024',
    name: 'Managing Resistance to Change',
    category: 'Change Management',
    description:
      'Leaders learn to anticipate where resistance comes from and work with it rather than against it during a transition.',
    relevance:
      'Gives managers concrete tactics for the pushback that follows reorganizations and process change.',
    topics: [
      'Sources of resistance',
      'Diagnosing team sentiment',
      'Negotiating adoption',
      'Sustaining momentum after launch',
    ],
    outcomes: [
      'Identify the root causes of resistance within a team.',
      'Select an intervention appropriate to the type of resistance observed.',
      'Build a plan to sustain adoption beyond the initial rollout.',
    ],
    durationHours: 2,
    levels: ['mid-level', 'senior-leader'],
    keywords: ['change management', 'conflict', 'stakeholder management'],
  },
  {
    id: 'organizational-transformation',
    courseId: 'course-v1:UOPX+OTPSKv1+Q12025',
    name: 'Planning Organizational Transformation',
    category: 'Change Management',
    description:
      'Senior leaders build an end-to-end transformation plan, from case for change through measurement of adoption.',
    relevance:
      'Positions transformation as a planned program rather than a series of announcements.',
    topics: [
      'Building the case for change',
      'Operating model design',
      'Sequencing and phasing',
      'Measuring adoption',
    ],
    outcomes: [
      'Construct a phased transformation roadmap for an organization.',
      'Define measures that show whether a transformation is landing.',
      'Align executive sponsors around a single change narrative.',
    ],
    durationHours: 3,
    levels: ['senior-leader', 'c-suite'],
    keywords: [
      'change management',
      'strategic thinking',
      'stakeholder management',
      'business acumen',
    ],
  },
  {
    id: 'active-empathetic-listening',
    courseId: 'course-v1:UOPX+AELSKv1+Q42023',
    name: 'Active and Empathetic Listening',
    category: 'Communication',
    description:
      'This module can help your employees become leaders who don’t just speak but listen and understand.',
    relevance:
      'Establishes the listening habits every other communication skill depends on.',
    topics: [
      'Critical elements of active and empathetic listening',
      'Demonstrating active listening techniques',
      'Different types of listening',
      'Real world scenario: active listening with clients',
    ],
    outcomes: [
      'Apply active and empathetic listening skills in different business situations.',
      'Recognize listening breakdowns as they happen and recover from them.',
    ],
    durationHours: 1.5,
    levels: ['individual-contributor', 'new-manager', 'mid-level'],
    keywords: ['communication', 'emotional intelligence', 'coaching'],
  },
  {
    id: 'leading-effective-communication',
    courseId: 'course-v1:UOPX+LECSKv1+Q22024',
    name: 'Leading with Effective Communication',
    category: 'Communication',
    description:
      'The module can help leaders become effective communicators adept at decision-making and inspiring and guiding their teams confidently and clearly.',
    relevance:
      'Builds leadership communication and emotional intelligence for managing teams effectively.',
    topics: [
      'Communicating with clarity',
      'Adjusting message to audience',
      'Communicating decisions',
      'Managing emotion in the moment',
    ],
    outcomes: [
      'Apply communication techniques that convey messages and foster a positive work environment.',
      'Determine methods to recognize and manage personal emotions and those of others.',
    ],
    durationHours: 2,
    levels: ['new-manager', 'mid-level'],
    keywords: ['communication', 'leadership', 'emotional intelligence'],
  },
  {
    id: 'difficult-conversations',
    courseId: 'course-v1:UOPX+DCSKv1+Q42024',
    name: 'Navigating Difficult Conversations',
    category: 'Communication',
    description:
      'A practical framework for the conversations managers avoid — performance, conflict, and change that isn’t welcome.',
    relevance:
      'Targets the single skill new managers most often report as their biggest gap.',
    topics: [
      'Preparing for a hard conversation',
      'Separating behavior from person',
      'De-escalation techniques',
      'Agreeing on what happens next',
    ],
    outcomes: [
      'Prepare and open a difficult conversation without escalating it.',
      'Close a conversation with a documented, mutual commitment.',
    ],
    durationHours: 2,
    levels: ['new-manager', 'mid-level'],
    keywords: ['conflict', 'communication', 'feedback'],
  },
  {
    id: 'feedback-that-lands',
    courseId: 'course-v1:UOPX+GRFSKv1+Q32024',
    name: 'Giving and Receiving Feedback',
    category: 'Communication',
    description:
      'Managers practice giving specific, timely feedback and receiving it without defensiveness.',
    relevance:
      'Makes performance conversations routine instead of annual and adversarial.',
    topics: [
      'Feedback models that work',
      'Timing and setting',
      'Receiving feedback well',
      'Following through',
    ],
    outcomes: [
      'Deliver specific feedback tied to observable behavior.',
      'Respond to critical feedback in a way that keeps the relationship intact.',
    ],
    durationHours: 1.5,
    levels: ['new-manager', 'mid-level'],
    keywords: ['feedback', 'performance management', 'communication'],
  },
  {
    id: 'adapting-leadership-styles',
    courseId: 'course-v1:UOPX+ALSSKv1+Q32024',
    name: 'Adapting Leadership Styles',
    category: 'Leadership Development',
    description:
      'Your team can refine their leadership skills for diverse professional environments like crisis management, organizational change and strategic planning.',
    relevance:
      'Helps newly promoted leaders find a style instead of copying the manager they had.',
    topics: [
      'Leadership styles',
      'Dynamic leadership adaptation',
      'Self-assessment tools',
      'Matching style to situation',
    ],
    outcomes: [
      'Identify a personal default leadership style and its blind spots.',
      'Select a leadership approach appropriate to the situation at hand.',
    ],
    durationHours: 2,
    levels: ['new-manager', 'mid-level'],
    keywords: ['leadership', 'change management', 'strategic thinking'],
  },
  {
    id: 'delegation-empowerment',
    courseId: 'course-v1:UOPX+DESKv1+Q22025',
    name: 'Delegation and Empowerment',
    category: 'Leadership Development',
    description:
      'For managers promoted out of individual contributor roles who are still doing the work themselves.',
    relevance:
      'Addresses the most common failure mode of first-time managers in their first year.',
    topics: [
      'What to delegate and what to keep',
      'Setting expectations',
      'Checking in without micromanaging',
      'Building team capability through delegation',
    ],
    outcomes: [
      'Decide which responsibilities to delegate and to whom.',
      'Set up a check-in rhythm that supports without controlling.',
    ],
    durationHours: 1.5,
    levels: ['new-manager'],
    keywords: ['delegation', 'leadership', 'team development'],
  },
  {
    id: 'coaching-for-performance',
    courseId: 'course-v1:UOPX+CFPSKv1+Q12025',
    name: 'Coaching for Performance',
    category: 'Leadership Development',
    description:
      'Managers learn a repeatable coaching model for one-on-ones, development plans, and performance recovery.',
    relevance:
      'Connects directly to retention — people stay for managers who develop them.',
    topics: [
      'The coaching conversation',
      'Running effective one-on-ones',
      'Development planning',
      'Coaching through underperformance',
    ],
    outcomes: [
      'Run a coaching conversation using a structured model.',
      'Build a development plan an employee actually owns.',
    ],
    durationHours: 2.5,
    levels: ['new-manager', 'mid-level'],
    keywords: ['coaching', 'performance management', 'feedback', 'retention'],
  },
  {
    id: 'reading-leading-room',
    courseId: 'course-v1:UOPX+RLRSKv1+Q42024',
    name: 'Reading and Leading the Room',
    category: 'Leadership Development',
    description:
      'Your team can learn about identifying different types of verbal and nonverbal communication cues, inspirational leadership techniques and more.',
    relevance:
      'Enhances leadership presence through reading group dynamics and interpersonal cues.',
    topics: [
      'Verbal and nonverbal cues',
      'Group dynamics',
      'Influencing without authority',
      'Inspirational leadership techniques',
    ],
    outcomes: [
      'Interpret verbal and non-verbal cues to enhance leadership effectiveness.',
      'Implement strategies to influence and inspire team members.',
    ],
    durationHours: 1.5,
    levels: ['mid-level', 'senior-leader'],
    keywords: ['executive presence', 'communication', 'leadership'],
  },
  {
    id: 'executive-presence',
    courseId: 'course-v1:UOPX+EPSKv1+Q42024',
    name: 'Building Executive Presence',
    category: 'Leadership Development',
    description:
      'Senior leaders develop the gravitas, clarity, and composure expected in board and executive settings.',
    relevance:
      'Written for the audience that already leads and now needs to be heard at the top table.',
    topics: [
      'Gravitas and composure',
      'Executive-level storytelling',
      'Presenting to a board',
      'Handling challenge in the room',
    ],
    outcomes: [
      'Deliver a position to an executive audience with clarity and brevity.',
      'Maintain composure under direct challenge.',
    ],
    durationHours: 2,
    levels: ['senior-leader', 'c-suite'],
    keywords: ['executive presence', 'communication', 'strategic thinking'],
  },
  {
    id: 'high-performing-teams',
    courseId: 'course-v1:UOPX+HPTSKv1+Q32024',
    name: 'Building High-Performing Teams',
    category: 'Team Development',
    description:
      'Leaders diagnose what is holding a team back and put the conditions for high performance in place.',
    relevance:
      'Moves the conversation from individual skill to team output, which is what the business measures.',
    topics: [
      'Conditions for team performance',
      'Psychological safety',
      'Team norms and rituals',
      'Diagnosing a stuck team',
    ],
    outcomes: [
      'Diagnose the constraint limiting a specific team’s performance.',
      'Establish team norms that hold under pressure.',
    ],
    durationHours: 2.5,
    levels: ['mid-level', 'senior-leader'],
    keywords: ['team development', 'leadership', 'retention'],
  },
  {
    id: 'emotional-intelligence',
    courseId: 'course-v1:UOPX+EIWSKv1+Q22024',
    name: 'Emotional Intelligence at Work',
    category: 'Professional Skills',
    description:
      'A foundation in self-awareness, regulation, and empathy that underpins every other people skill.',
    relevance:
      'A low-risk shared starting point when a cohort has mixed experience levels.',
    topics: [
      'Self-awareness',
      'Self-regulation',
      'Empathy in practice',
      'Social awareness at work',
    ],
    outcomes: [
      'Recognize personal emotional triggers in a work context.',
      'Apply empathy deliberately in day-to-day interactions.',
    ],
    durationHours: 1.5,
    levels: ['individual-contributor', 'new-manager', 'mid-level'],
    keywords: ['emotional intelligence', 'communication', 'retention'],
  },
  {
    id: 'stakeholder-management',
    courseId: 'course-v1:UOPX+SMSKv1+Q22024',
    name: 'Stakeholder Management',
    category: 'Project Management',
    description:
      'Employees can explore a comprehensive overview of effective stakeholder management with skills to identify, analyze and prioritize stakeholders.',
    relevance:
      'Covers stakeholder identification, analysis, prioritization and tailored engagement strategies.',
    topics: [
      'Project lifecycle',
      'Stakeholder analysis and prioritization',
      'Effective stakeholder communication',
      'Stakeholder engagement strategies',
    ],
    outcomes: [
      'Prioritize stakeholders based on importance and potential impact.',
      'Devise tailored engagement strategies for different stakeholder groups.',
    ],
    durationHours: 2,
    levels: ['mid-level', 'senior-leader'],
    keywords: [
      'stakeholder management',
      'project management',
      'communication',
    ],
  },
  {
    id: 'enhancing-business-outcomes',
    courseId: 'course-v1:UOPX+EBOSKv1+Q12024',
    name: 'Enhancing Business Outcomes with Effective Leadership',
    category: 'Business Acumen',
    description:
      'Employees will learn how to align leadership styles with goals, enhance collaboration, manage performance and drive accountability.',
    relevance:
      'Develops strategic leadership techniques to align teams with business goals and outcomes.',
    topics: [
      'Aligning leadership to business goals',
      'Driving accountability',
      'Managing performance',
      'Collaboration across functions',
    ],
    outcomes: [
      'Analyze how leadership styles influence business outcomes.',
      'Apply techniques to improve team dynamics, engagement and business results.',
    ],
    durationHours: 2.5,
    levels: ['senior-leader', 'c-suite'],
    keywords: [
      'leadership',
      'business acumen',
      'performance management',
      'strategic thinking',
    ],
  },
  {
    id: 'strategic-decision-making',
    courseId: 'course-v1:UOPX+SDMSKv1+Q12025',
    name: 'Strategic Thinking and Decision-Making',
    category: 'Business Acumen',
    description:
      'Executives practice framing ambiguous problems, weighing trade-offs, and committing to decisions with incomplete information.',
    relevance:
      'Pitched at the altitude C-suite buyers expect — trade-offs, not techniques.',
    topics: [
      'Framing the real problem',
      'Evaluating trade-offs',
      'Deciding under uncertainty',
      'Communicating the decision',
    ],
    outcomes: [
      'Frame an ambiguous business problem so it can be decided on.',
      'Commit to and communicate a decision made with incomplete information.',
    ],
    durationHours: 3,
    levels: ['senior-leader', 'c-suite'],
    keywords: ['strategic thinking', 'decision making', 'business acumen'],
  },
]

export const moduleById = (id: string): SkillModule | undefined =>
  skillModules.find((m) => m.id === id)
